<script setup lang="ts">
import OrderForm from '@/components/features/orders/OrderForm.vue'
import CustomerDetailsForm from '@/components/features/orders/CustomerDetailsForm.vue'
import ProductDetailsForm from '@/components/features/orders/ProductDetailsForm.vue'
import { useRouter, RouterLink } from 'vue-router'
import { useOrderForm } from '@/composables/useOrderForm'
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import { ref, onMounted, computed } from 'vue'
import InputSwitch from 'primevue/inputswitch'
import InputLabel from '@/components/inputs/InputLabel.vue'
import { type Customer } from '@/types/customer-types'
import {type PackingList} from '@/types/packingList-types'
import { getCustomerList } from '@/api/customers'
import {getRollsList} from "@/api/rolls";
import PackingListDialogue from "@/components/features/orders/PackingListDialog.vue";
import { getPackingList } from '@/api/packingList'
import FormTextArea from '@/components/inputs/FormTextArea.vue'
import { cancelOrder, uncancelOrder, getOrderById } from '@/api/orders'
import { useToast } from 'primevue/usetoast'
const router = useRouter()
const checked = ref(true)
const customerList = ref<Customer[]>([])
const { values, handleSubmit, setErrors, formType, newProduct, removeProduct, productFields } = useOrderForm('view')
const toast = useToast()

const conformityOptions = [
  {
    id: '1',
    name: 'Download As PDF'
  },
  {
    id: '2',
    name: 'Download As Microsoft Word'
  }
]

const rollList = ref<any>([])
const packingList = ref<PackingList[]>([])
const orderId = router.currentRoute.value.params.id
const showAddPackingList = ref(false)
const orderData = ref<any>(null)
const handleEditOrder = () => {
  router.push(`/orders/${orderId}/edit`)
}

const handleToggleCancel = async () => {
  try {
    const orderIdStr = Array.isArray(orderId) ? orderId[0] : orderId
    if (isCancelled.value) {
      // Restore the order
      await uncancelOrder(orderIdStr)
      toast.add({ severity: 'success', summary: 'Order Restored!', detail: 'The order has been successfully restored.', life: 3000 })
      // Refresh the order data
      orderData.value = await getOrderById(orderIdStr)
    } else {
      // Cancel the order
      await cancelOrder(orderIdStr)
      toast.add({ severity: 'success', summary: 'Order Cancelled!', detail: 'The order has been successfully cancelled.', life: 3000 })
      // Refresh the order data
      orderData.value = await getOrderById(orderIdStr)
    }
  } catch (error: any) {
    const action = isCancelled.value ? 'Restore' : 'Cancel'
    toast.add({ severity: 'error', summary: `${action} Failed!`, detail: error.response?.data?.message || `Failed to ${action.toLowerCase()} order`, life: 3000 })
  }
}

onMounted(async () => {
  try {
    const orderIdStr = Array.isArray(orderId) ? orderId[0] : orderId
    orderData.value = await getOrderById(orderIdStr)
  } catch (error) {
    console.error('Error loading order data:', error)
  }
  rollList.value = await getRollsList()
  customerList.value = await getCustomerList()
  packingList.value = await getPackingList()
})

const isCancelled = computed(() => {
  const fromValues = values?.isCancelled === true
  const fromDirectData = orderData.value?.isCancelled === true
  return fromValues || fromDirectData
})

const computedPackingList = computed(() => {
  return packingList.value.filter((item) => item.orderId === orderId)
})
</script>

<template>
  <div class="flex justify-between items-center mb-10">
    <RouterLink to="/orders" class="flex items-center gap-x-2 group hover:text-primary-default">
      <Icon
        icon="material-symbols:arrow-back-rounded"
        class="text-3xl group-hover:translate-x-2 transition-transform"
      />
      <span>Back</span>
    </RouterLink>

    <h1 class="font-semibold text-2xl">Customer Order</h1>

    <div class="flex gap-2 flex-col">
      <Button
          label="Create Packing List"
          @click="
          () => {
            showAddPackingList = true
          }
        "
      >
        <template #icon>
          <Icon icon="mdi:add-to-photos" class="text-xl ml-3 order-1" />
        </template>
      </Button>
      <Button
        outlined
        class="flex items-center gap-x-2"
        @click="handleToggleCancel"
      >
        <span>{{ isCancelled ? 'Restore Order' : 'Cancel Order' }}</span>
        <Icon :icon="isCancelled ? 'material-symbols:undo' : 'material-symbols:cancel'" class="text-xl" />
      </Button>
      <Button outlined label="Edit Order" @click="handleEditOrder">
        <template #icon>
          <Icon icon="basil:edit-solid" class="text-xl ml-3 order-1" />
        </template>
      </Button>
    </div>
  </div>
  <div class="active rounded-xl px-8 py-4 border border-primary-500 mb-8">
    <h3 class="font-semibold text-xl text-primary-500 mb-2">Packing List from the order</h3>
    <div class="flex flex-col items-start gap-3">
      <div class="flex items-center gap-x-2 col-span-2">
        <div v-if="computedPackingList.length > 0">
          <InputSwitch v-model="checked" />
          <InputLabel class="!mb-0 text-xs text-primary-500">All packing lists have been fulfilled</InputLabel>
        </div>
        <div v-else>
          <InputLabel class="!mb-0 text-xs font-bold text-primary-500">No packing lists have been fulfilled</InputLabel>
        </div>
      </div>
      <div class="flex flex-wrap gap-2 text-primary-500">
        <div class="flex flex-wrap items-center gap-1 text-primary-500">
          <template v-for="(packingList, index) in computedPackingList" :key="packingList.packingListId">
            <RouterLink
              :to="`/packingList/${packingList.id}`"
              class="hover:underline hover:text-primary-700"
            >
              {{ packingList.packingListId }}
            </RouterLink>
            <span v-if="index < computedPackingList.length - 1">&gt;</span>
          </template>
        </div>
      </div>
    </div>
  </div>
  <div class="bg-white rounded-xl pb-2">
    <OrderForm :type="formType" :values="values" :onSubmit="handleSubmit" :setErrors="setErrors" />
    <CustomerDetailsForm
    :customer-list="customerList"
      :type="formType"
      :values="values"
      :onSubmit="handleSubmit"
      :setErrors="setErrors"
    />
    <p class="text-lg font-bold ml-7">Product Details ({{ productFields.length }})</p>
    <template v-for="(product, index) in productFields" :key="product.key">
    <ProductDetailsForm
      :customer-list="customerList"
      :type="formType"
       :formPath="`${index}`"
       :newProduct="newProduct"
       :productFields="values?.orderProducts[index]"
       :removeProduct="() => removeProduct(index)"
       :showRemoveButton="false"
      :values="values"
      :onSubmit="handleSubmit"
      :setErrors="setErrors"
    />
    </template>
    <div class="p-5">
      <FormTextArea
        label="Delivery Information"
        inputName="deliveryInformation"
        id="deliveryInformation"
        :disabled="true"
      />
    </div>
    <PackingListDialogue
        :visible="showAddPackingList"
        :comformityOptions="conformityOptions"
        :orderedProductsOptions="values?.orderProducts"
        :rolls="rollList"
        :close="() => {showAddPackingList = false}"
        :orderNumber="values?.orderNumber"
    />
  </div>
</template>
